import type { AxiosPromise } from "axios";
import { route } from "../../shim/jsbridge";

import { AxiosApiWrapper } from "../../services/api/AxiosApiWrapper";
import type { CheckPdfResponse } from "../samples/@types/pdf";
import {
  DnsReport,
  DomainProp,
  DomainReport,
  DomainResolutionsResponse,
  DomainUrlsResponse,
  IPDomainResolutionsResponse,
  IPDownloadedFiles,
  IPProp,
  IPReport,
  IPUrlsResponse,
  JoeURLSubmissionResponse,
  PayloadsReport,
  QueryParamProps,
  RelatedDomainsResponse,
  UrlReport
} from "./@types";
import { URLAnalysisStatus, UrlIsAnalyzing } from "./@types/analysis-status";
import { NetworkClassification, RequestWithCancel } from "./@types/common";
import {
  DomainWithCursorPagination,
  DomainWithIdPagingRequest,
  EnrichedDomainResolutionsResponse
} from "./@types/Domain";
import {
  IPFilesRequest,
  IPResolutionsResponse,
  IPWithIdPagingRequest
} from "./@types/IP";
import { NetworkReputationReport, UrlDom, UrlFilesRequest } from "./@types/Url";

type PaginationRequest = {
  page?: string;
  pageSize?: string | number;
};

type DownloadedFilesRequest = {
  extended?: boolean;
  classification?: NetworkClassification;
} & PaginationRequest;

class Api extends AxiosApiWrapper {
  urlReputation = (data: unknown): AxiosPromise<NetworkReputationReport> =>
    this.post(route("api-network-reputation"), data);
  urlReport = ({
    url,
    signal
  }: {
    url: string;
    signal?: AbortSignal;
  }): AxiosPromise<UrlReport> =>
    this.get(route("api-network-threat-intel-url", {}, { url }), { signal });

  domainReport = ({ domain }: { domain: string }): AxiosPromise<DomainReport> =>
    this.get(route("api-network-threat-intel-domain", [domain]));

  bulkUrlReport = (
    urls: Array<string>,
    signal: AbortSignal
  ): AxiosPromise<{ reports: Array<UrlReport> }> =>
    this.post(
      route("api-network-threat-intel-url-report-bulk"),
      { urls },
      { signal }
    );

  bulkDomainReport = (
    domains: Array<string>,
    signal: AbortSignal
  ): AxiosPromise<{ reports: Array<DomainReport> }> =>
    this.post(
      route("api-network-threat-intel-domain-report-bulk"),
      { domains },
      { signal }
    );

  bulkIPReport = (
    ips: Array<string>,
    signal: AbortSignal
  ): AxiosPromise<{ reports: Array<IPReport> }> =>
    this.post(
      route("api-network-threat-intel-ip-report-bulk"),
      { ips },
      { signal }
    );

  ipReport = ({ ip }: { ip: string }): AxiosPromise<IPReport> =>
    this.get(route("api-network-threat-intel-ip-report", { value: ip }));

  dnsReport = ({ url }: { url: string }): AxiosPromise<DnsReport> =>
    this.post(route("api-network-threat-intel-dns"), {
      url
    });

  getUrlPayloads = ({
    url,
    queryParams
  }: {
    url: string;
    queryParams: QueryParamProps;
  }): AxiosPromise<PayloadsReport> =>
    this.post(route("api-sample-browse-url-payloads", [url], queryParams), {
      url
    });

  ipResolutions = ({
    ip,
    page,
    pageSize
  }: {
    ip: string;
    page?: string;
    pageSize?: number;
  }): AxiosPromise<IPDomainResolutionsResponse> =>
    this.get(
      route(
        "api-network-threat-intel-ip-resolutions",
        { value: ip },
        {
          ...(page && { page }),
          ...(pageSize && { page_size: pageSize })
        }
      )
    );

  enrichedIPResolutions = ({
    ip,
    page,
    page_size,
    signal
  }: RequestWithCancel<IPWithIdPagingRequest>): AxiosPromise<IPResolutionsResponse> =>
    this.get(
      route(
        "api-network-threat-intel-ip-resolutions-enriched",
        { value: ip },
        {
          ...(page && { page }),
          ...(page_size && { page_size })
        }
      ),
      { signal }
    );

  domainResolutions = ({
    domain,
    page,
    pageSize
  }: DomainProp & PaginationRequest): AxiosPromise<DomainResolutionsResponse> =>
    this.get(
      route(
        "api-network-threat-intel-domain-resolutions",
        { value: domain },
        {
          ...(page && { page }),
          ...(pageSize && { page_size: pageSize })
        }
      )
    );

  enrichedDomainResolutions = ({
    domain,
    page,
    page_size,
    signal
  }: RequestWithCancel<DomainWithIdPagingRequest>): AxiosPromise<EnrichedDomainResolutionsResponse> =>
    this.get(
      route(
        "api-network-threat-intel-domain-resolutions-enriched",
        { value: domain },
        {
          ...(page && { page }),
          ...(page_size && { page_size })
        }
      ),
      { signal }
    );

  domainRelatedDomains = ({
    domain,
    page,
    pageSize
  }: DomainProp & PaginationRequest): AxiosPromise<RelatedDomainsResponse> =>
    this.get(
      route(
        "api-network-threat-intel-domain-related-domains",
        { value: domain },
        {
          ...(page && { page }),
          ...(pageSize && { page_size: pageSize })
        }
      )
    );

  domainUrls = ({
    domain,
    page,
    pageSize
  }: DomainProp & PaginationRequest): AxiosPromise<DomainUrlsResponse> =>
    this.get(
      route(
        "api-network-threat-intel-domain-urls",
        { value: domain },
        {
          ...(page && { page }),
          ...(pageSize && { page_size: pageSize })
        }
      )
    );

  ipURLs = ({
    ip,
    page,
    pageSize
  }: {
    ip: string;
    page?: string;
    pageSize?: number;
  }): AxiosPromise<IPUrlsResponse> =>
    this.get(
      route(
        "api-network-threat-intel-ip-urls",
        { value: ip },
        {
          ...(page && { page }),
          ...(pageSize && { page_size: pageSize })
        }
      )
    );

  domainDownloadedFiles = ({
    domain,
    page,
    pageSize,
    extended,
    classification
  }: DomainProp & DownloadedFilesRequest) =>
    this.get(
      route(
        "api-network-threat-intel-domain-downloaded-files",
        { value: domain },
        {
          ...(page && { page }),
          ...(pageSize && { page_size: pageSize }),
          ...(extended && { extended }),
          ...(classification && {
            classification: classification.toUpperCase()
          })
        }
      )
    );

  enrichedDomainDownloadedFiles = ({
    domain,
    page,
    pageSize,
    extended,
    classification,
    signal
  }: RequestWithCancel<DomainProp & DownloadedFilesRequest>) =>
    this.get(
      route(
        "api-network-threat-intel-domain-downloaded-files-enriched",
        { value: domain },
        {
          ...(page && { page }),
          ...(pageSize && { page_size: pageSize }),
          ...(extended && { extended }),
          ...(classification && {
            classification: classification.toUpperCase()
          })
        }
      ),
      { signal }
    );

  enrichedIpDownloadedFiles = ({
    ip,
    page,
    pageSize,
    extended,
    classification
  }: IPProp & DownloadedFilesRequest) =>
    this.get(
      route(
        "api-network-threat-intel-ip-downloaded-files-enriched",
        { value: ip },
        {
          ...(page && { page }),
          ...(pageSize && { page_size: pageSize }),
          ...(extended && { extended }),
          ...(classification && {
            classification: classification.toUpperCase()
          })
        }
      )
    );

  urlDownloadedFiles = ({
    url,
    analysisId,
    source,
    page,
    pageSize,
    extended,
    classification
  }: {
    url?: string;
    analysisId?: string;
    source?: string;
  } & DownloadedFilesRequest) =>
    this.post(route("api-network-threat-intel-url-downloaded-files"), {
      ...(analysisId ? { analysis_id: analysisId } : { url }),
      ...(source && { source }),
      ...(page && { page }),
      ...(pageSize && { page_size: pageSize }),
      ...(extended && { extended }),
      ...(classification && { classification: classification.toUpperCase() })
    });

  ipDownloadedFiles = ({
    ip,
    page,
    pageSize,
    extended,
    classification
  }: {
    ip: string;
  } & DownloadedFilesRequest): AxiosPromise<IPDownloadedFiles> =>
    this.get(
      route(
        "api-network-threat-intel-ip-downloaded-files",
        { value: ip },
        {
          ...(page && { page }),
          ...(pageSize && { page_size: pageSize }),
          ...(extended && { extended }),
          ...(classification && {
            classification: classification.toUpperCase()
          })
        }
      )
    );

  urlDynamicAnalysisStatus = (
    url: string,
    signal: AbortSignal
  ): AxiosPromise<URLAnalysisStatus> =>
    this.post(
      route("api_rl_dynamic_analysis_url_report_display_status"),
      {
        url
      },
      { signal }
    );

  getAnalysisStatus = (url: string, signal: AbortSignal) =>
    this.post<UrlIsAnalyzing>(
      route("url_analysis_status"),
      { url },
      { signal }
    );

  getDomPreviewAndLinks = ({
    url,
    domUrl
  }: {
    url: string;
    domUrl?: string;
  }): AxiosPromise<UrlDom> =>
    this.get(route("api-network-threat-intel-url-dom"), {
      params: {
        url,
        dom_url: domUrl
      }
    });

  getJoeUrlSubmission = (url: string): AxiosPromise<JoeURLSubmissionResponse> =>
    this.get(route("api_joe_url_submission", [], { url }));

  postFalseReport = (
    url: string,
    type: string,
    comment: string,
    contact?: string
  ) =>
    this.post(route("api-network-threat-intel-false-report"), {
      url,
      type,
      comment,
      contact
    });

  filesDetails = (
    hashes: Array<string>,
    location: string,
    signal: AbortSignal
  ) =>
    this.post(
      route("api-network-threat-intel-downloaded-files-details"),
      { hashes, location },
      { signal }
    );

  filesReferencingUrl = (request: UrlFilesRequest, signal: AbortSignal) =>
    this.post(
      route("api-network-threat-intel-files-referencing-domain"),
      request,
      { signal }
    );

  filesCommunicatingWithUrl = (request: UrlFilesRequest, signal: AbortSignal) =>
    this.post(
      route("api-network-threat-intel-files-communicating-domain"),
      request,
      { signal }
    );

  filesReferencingIP = (request: IPFilesRequest, signal: AbortSignal) =>
    this.post(route("api-network-threat-intel-files-referencing-ip"), request, {
      signal
    });

  filesCommunicatingWithIP = (request: IPFilesRequest, signal: AbortSignal) =>
    this.post(
      route("api-network-threat-intel-files-communicating-ip"),
      request,
      { signal }
    );

  enrichedDomainUrls = (
    request: DomainWithCursorPagination,
    signal: AbortSignal
  ) =>
    this.get(
      route("api-network-threat-intel-domain-urls-enriched", {
        value: request.domain
      }),
      {
        params: {
          ...(request.page ? { page: request.page } : {}),
          page_size: request.page_size
        },
        signal
      }
    );

  enrichedRelatedDomains = (
    request: DomainWithCursorPagination,
    signal: AbortSignal
  ) =>
    this.get(
      route("api-network-threat-intel-domain-related-domains-enriched", {
        value: request.domain
      }),
      {
        params: {
          ...(request.page ? { page: request.page } : {}),
          page_size: request.page_size
        },
        signal
      }
    );

  enrichedIpUrls = (request: IPWithIdPagingRequest, signal: AbortSignal) =>
    this.get(
      route("api-network-threat-intel-ip-urls-enriched", {
        value: request.ip
      }),
      {
        params: {
          ...(request.page ? { page: request.page } : {}),
          page_size: request.page_size
        },
        signal
      }
    );

  makeUrlPdf = (encodedUrl: string) =>
    this.get(route("pdf_url_summary_make", { encoded_url: encodedUrl }));

  urlPdfStatusOrFile = (
    encodedUrl: string
  ): AxiosPromise<CheckPdfResponse | Blob> =>
    this.get(route("pdf_url_summary_get", { encoded_url: encodedUrl }));
}

export default new Api();
